import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';

const OracleInvitation = ({ onAccept }) => {
  const [phase, setPhase] = useState('flying'); // 'flying' | 'settled' | 'accepted' | 'dismissed'
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [isMuted, setIsMuted] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const utteranceRef = useRef(null);
  const speechSupportedRef = useRef(typeof window !== 'undefined' && 'speechSynthesis' in window);

  const introText = "Welcome to the Next Federation. I can give you a tour. Would you like to know all the millions of things that we offer and all the cool stuff? I'd like to show you around.";
  const words = introText.split(' ');

  const highlightWordsWithTiming = () => {
    let index = 0;
    const interval = setInterval(() => {
      setCurrentWordIndex(index);
      index++;
      if (index >= words.length) {
        clearInterval(interval);
        setCurrentWordIndex(-1);
        setTimeout(() => setShowButtons(true), 500);
      }
    }, 200);
  };

  const startSpeech = () => {
    if (!speechSupportedRef.current || isMuted) {
      // Fallback: timed word highlighting
      highlightWordsWithTiming();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(introText);
    utterance.rate = 0.9;
    utterance.pitch = 1.1;
    utterance.volume = isMuted ? 0 : 1;

    let wordIndex = 0;
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        const charIndex = event.charIndex;
        const textUpToNow = introText.substring(0, charIndex);
        wordIndex = textUpToNow.split(' ').length - 1;
        setCurrentWordIndex(wordIndex);
      }
    };

    utterance.onend = () => {
      setCurrentWordIndex(-1);
      setTimeout(() => setShowButtons(true), 500);
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    // Check if user has already seen the invitation
    const hasSeenInvitation = localStorage.getItem('oracle_invitation_seen');
    if (hasSeenInvitation) {
      setPhase('dismissed');
      return;
    }

    // Capture ref value for cleanup
    const speechSupported = speechSupportedRef.current;

    // Flying phase for 3 seconds, then settle
    const settleTimer = setTimeout(() => {
      setPhase('settled');
      // Start speech after settling animation completes
      setTimeout(() => {
        startSpeech();
      }, 800);
    }, 3000);

    return () => {
      clearTimeout(settleTimer);
      if (utteranceRef.current && speechSupported) {
        window.speechSynthesis.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (speechSupportedRef.current && utteranceRef.current) {
      window.speechSynthesis.cancel();
      if (!newMuted) {
        startSpeech();
      }
    }
  };

  const handleAccept = () => {
    setPhase('accepted');
    localStorage.setItem('oracle_invitation_seen', 'true');
    if (speechSupportedRef.current) {
      window.speechSynthesis.cancel();
    }
    if (onAccept) {
      onAccept();
    }
  };

  const handleDismiss = () => {
    setPhase('dismissed');
    localStorage.setItem('oracle_invitation_seen', 'true');
    if (speechSupportedRef.current) {
      window.speechSynthesis.cancel();
    }
  };

  if (phase === 'dismissed' || phase === 'accepted') return null;

  return (
    <AnimatePresence>
      {(phase === 'flying' || phase === 'settled') && (
        <div 
          className="fixed inset-0 z-60 flex items-center justify-center"
          style={{ pointerEvents: 'none' }}
        >
        {/* Flying Bot with Particle Trail */}
        {phase === 'flying' && (
          <>
            {/* Particle Trail */}
            <motion.div
              className="absolute w-3 h-3 rounded-full bg-[#8A2BE2]"
              animate={{
                x: [50, 200, 400, 600, 800, 600, 400, window.innerWidth / 2 - 50],
                y: [100, 50, 150, 80, 200, 300, 250, window.innerHeight / 2 - 50],
                opacity: [0.6, 0.4, 0.6, 0.3, 0.5, 0.4, 0.6, 0],
                scale: [1, 0.8, 1, 0.7, 1, 0.8, 1, 0]
              }}
              transition={{ duration: 3, ease: "easeInOut" }}
              style={{ 
                filter: 'blur(2px)',
                boxShadow: '0 0 15px rgba(138, 43, 226, 0.8)'
              }}
            />
            <motion.div
              className="absolute w-2.5 h-2.5 rounded-full bg-[#00F0FF]"
              animate={{
                x: [30, 180, 380, 580, 780, 580, 380, window.innerWidth / 2 - 60],
                y: [110, 60, 160, 90, 210, 310, 260, window.innerHeight / 2 - 40],
                opacity: [0.5, 0.3, 0.5, 0.2, 0.4, 0.3, 0.5, 0],
                scale: [1, 0.6, 1, 0.5, 1, 0.6, 1, 0]
              }}
              transition={{ duration: 3, ease: "easeInOut", delay: 0.1 }}
              style={{ 
                filter: 'blur(2px)',
                boxShadow: '0 0 12px rgba(0, 240, 255, 0.7)'
              }}
            />

            {/* Flying Oracle Bot */}
            <motion.div
              className="absolute"
              animate={{
                x: [60, 210, 410, 610, 810, 610, 410, window.innerWidth / 2 - 40],
                y: [90, 40, 140, 70, 190, 290, 240, window.innerHeight / 2 - 40],
                scale: [0.3, 0.35, 0.4, 0.35, 0.4, 0.5, 0.7, 1]
              }}
              transition={{ duration: 3, ease: [0.43, 0.13, 0.23, 0.96] }}
            >
              <div 
                className="w-20 h-20 rounded-full overflow-hidden shadow-2xl border-2 border-[#00F0FF]/60"
                style={{
                  boxShadow: '0 0 30px rgba(0, 240, 255, 0.8), 0 0 60px rgba(0, 240, 255, 0.5)'
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/9e02c4e5c5054c27840e88b9c05ce8f3_IMG_4058.jpeg"
                  alt="Oracle Bot"
                  className="w-full h-full object-cover"
                  style={{
                    filter: 'hue-rotate(180deg) saturate(1.5) brightness(1.1)'
                  }}
                />
              </div>
            </motion.div>
          </>
        )}

        {/* Settled Bot with Text Card */}
        {phase === 'settled' && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col items-center gap-4"
            style={{ pointerEvents: 'auto' }}
          >
            {/* Oracle Bot Avatar with Rotation */}
            <motion.div
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative"
            >
              {/* Pulsing Glow */}
              <motion.div
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.4, 0.7, 0.4]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(138,43,226,0.8) 0%, rgba(0,240,255,0.5) 50%, transparent 70%)',
                  filter: 'blur(15px)'
                }}
              />
              <div 
                className="relative w-24 h-24 rounded-full flex items-center justify-center shadow-2xl border-2 border-[#00F0FF]/60"
                style={{
                  background: 'linear-gradient(135deg, #4B0082 0%, #8A2BE2 50%, #00F0FF 100%)',
                  boxShadow: '0 0 40px rgba(138, 43, 226, 1), 0 0 80px rgba(0, 240, 255, 0.6)'
                }}
              >
                <span className="text-5xl font-black text-white" style={{ textShadow: '0 0 25px rgba(0,240,255,1)' }}>O</span>
              </div>
            </motion.div>

            {/* Frosted Glass Text Card */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="relative w-full max-w-lg mx-4 sm:w-[90%] md:w-auto"
            >
              <div 
                className="p-6 rounded-2xl border border-[#8A2BE2]/40 shadow-2xl"
                style={{
                  background: 'rgba(30, 10, 50, 0.85)',
                  backdropFilter: 'blur(20px)'
                }}
              >
                {/* Mute Toggle */}
                <button
                  onClick={toggleMute}
                  className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 transition-colors"
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? (
                    <SpeakerSlash size={20} className="text-gray-400" />
                  ) : (
                    <SpeakerHigh size={20} className="text-[#00F0FF]" />
                  )}
                </button>

                {/* Scrolling Text with Word Highlighting */}
                <div className="max-h-32 overflow-y-auto pr-8 custom-scrollbar">
                  <p className="text-base leading-relaxed text-white">
                    {words.map((word, index) => (
                      <span
                        key={index}
                        className={`transition-all duration-200 ${
                          index === currentWordIndex
                            ? 'bg-gradient-to-r from-yellow-400 to-yellow-300 text-black px-1 rounded font-bold'
                            : ''
                        }`}
                      >
                        {word}{' '}
                      </span>
                    ))}
                  </p>
                </div>

                {/* Action Buttons */}
                {showButtons && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex gap-3 mt-6 justify-center"
                  >
                    <button
                      onClick={handleAccept}
                      className="px-6 py-2.5 bg-gradient-to-r from-[#00F0FF] to-[#8A2BE2] text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
                      style={{ boxShadow: '0 0 20px rgba(0,240,255,0.5)' }}
                    >
                      Yes, show me around!
                    </button>
                    <button
                      onClick={handleDismiss}
                      className="px-6 py-2.5 border-2 border-gray-500 text-gray-300 font-medium rounded-full hover:bg-white/5 hover:border-gray-400 transition-all"
                    >
                      Maybe later
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(138, 43, 226, 0.6);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(138, 43, 226, 0.9);
        }
      `}</style>
    </AnimatePresence>
  );
};

export default OracleInvitation;
